import { Box, Flex, Image } from "@chakra-ui/react";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { format } from "date-fns";
import { FC } from "react";

import {
  Table,
  TableDataColumn,
  TableDataRow,
  TableHeaderColumn,
  TableHeaderRow,
} from "~/src/components/common/tables/Table";
import { Tooltip } from "~/src/components/common/Tooltip";
import { chakraFactory } from "~/src/lib/chakra-ui";

import { ContentPreviewElement } from "./ContentPreviewImage";

export type ContentElementMetric = ContentPreviewElement & {
  name: string;
  previewImageUrl: string | null | undefined;
  updatedDate: string;
  views: number;
  viewRate: number;
  averageViewTime: number;
  goals: number;
  goalRate: number;
  clicks: number;
  clickRate: number;
};

export type Props = {
  elements: ContentElementMetric[];
};

const GroupHeaderCell = chakraFactory(Box, {
  baseStyle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    fontWeight: "500",
    fontSize: "12px",
    lineHeight: "17px",
    color: "#282828",
    padding: "0 14px",
    borderRight: "1px solid #DADADA",
  },
});

const formatRate = (rate: number): string => `${parseFloat(rate.toFixed(2))}%`;

const formatViewTime = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (hours > 0) return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(minutes)}:${pad(seconds)}`;
};

const formatDate = (iso: string): string => {
  try {
    return format(new Date(iso), "yyyy-MM-dd");
  } catch {
    return iso;
  }
};

export const ContentMetricsTable: FC<Props> = ({ elements }) => (
  <Table>
    <TableHeaderRow>
      <GroupHeaderCell flex={3} minWidth="450px">
        コンテンツ
      </GroupHeaderCell>
      <GroupHeaderCell flex={3} minWidth="450px">
        ビュー
      </GroupHeaderCell>
      <GroupHeaderCell flex={2} minWidth="300px">
        ゴール
      </GroupHeaderCell>
      <GroupHeaderCell flex={2} minWidth="300px" borderRight="none">
        クリック
      </GroupHeaderCell>
    </TableHeaderRow>
    <TableHeaderRow withColumnNumbersRow>
      <TableHeaderColumn>プレビュー</TableHeaderColumn>
      <TableHeaderColumn>コンテンツ名</TableHeaderColumn>
      <TableHeaderColumn>最終更新</TableHeaderColumn>
      <TableHeaderColumn>数</TableHeaderColumn>
      <TableHeaderColumn>
        <Flex alignItems="center" gap={1}>
          率
          <Tooltip
            label="ビュー率 = ビュー数 ÷ ユニークユーザー数"
            placement="top"
          >
            <Box as="span" display="inline-flex" cursor="pointer">
              <HelpOutlineOutlinedIcon sx={{ fontSize: 14, color: "#999" }} />
            </Box>
          </Tooltip>
        </Flex>
      </TableHeaderColumn>
      <TableHeaderColumn>
        <Flex alignItems="center" gap={1}>
          秒数
          <Tooltip
            label="平均ビュー時間 = 総ビュー時間 ÷ ビュー数"
            placement="top"
          >
            <Box as="span" display="inline-flex" cursor="pointer">
              <HelpOutlineOutlinedIcon sx={{ fontSize: 14, color: "#999" }} />
            </Box>
          </Tooltip>
        </Flex>
      </TableHeaderColumn>
      <TableHeaderColumn>数</TableHeaderColumn>
      <TableHeaderColumn>
        <Flex alignItems="center" gap={1}>
          率
          <Tooltip label="ゴール率 = ゴール数 ÷ ビュー数" placement="top">
            <Box as="span" display="inline-flex" cursor="pointer">
              <HelpOutlineOutlinedIcon sx={{ fontSize: 14, color: "#999" }} />
            </Box>
          </Tooltip>
        </Flex>
      </TableHeaderColumn>
      <TableHeaderColumn>
        <Flex alignItems="center" gap={1}>
          数
          <Tooltip
            label={
              "同一ユーザーセッション内の複数回クリックは、コンテンツごとに1クリックとしてカウントされます。\n表示開始から2秒以内にクリックが発生した場合：\n・クリックのみカウント\n・ビューはカウントしない"
            }
            placement="top"
          >
            <Box as="span" display="inline-flex" cursor="pointer">
              <HelpOutlineOutlinedIcon sx={{ fontSize: 14, color: "#999" }} />
            </Box>
          </Tooltip>
        </Flex>
      </TableHeaderColumn>
      <TableHeaderColumn>
        <Flex alignItems="center" gap={1}>
          率
          <Tooltip label="クリック率 = クリック数 ÷ ビュー数" placement="top">
            <Box as="span" display="inline-flex" cursor="pointer">
              <HelpOutlineOutlinedIcon sx={{ fontSize: 14, color: "#999" }} />
            </Box>
          </Tooltip>
        </Flex>
      </TableHeaderColumn>
    </TableHeaderRow>
    {elements.length === 0 ? (
      <TableDataRow>
        <TableDataColumn
          type="text"
          data="データがありません"
          flex={10}
          justifyContent="center"
        />
      </TableDataRow>
    ) : (
      elements.map((element, i) => (
        <TableDataRow key={i}>
          <TableDataColumn
            type="text"
            data={
              element.previewImageUrl ? (
                <Image
                  src={element.previewImageUrl}
                  boxSize="48px"
                  objectFit="contain"
                />
              ) : null
            }
          />
          <TableDataColumn type="text" data={element.name} />
          <TableDataColumn type="text" data={formatDate(element.updatedDate)} />
          <TableDataColumn
            type="numeric"
            data={element.views}
            justifyContent="center"
          />
          <TableDataColumn type="text" data={formatRate(element.viewRate)} />
          <TableDataColumn
            type="text"
            data={formatViewTime(element.averageViewTime)}
          />
          <TableDataColumn
            type="numeric"
            data={element.goals}
            justifyContent="center"
          />
          <TableDataColumn type="text" data={formatRate(element.goalRate)} />
          <TableDataColumn
            type="numeric"
            data={element.clicks}
            justifyContent="center"
          />
          <TableDataColumn type="text" data={formatRate(element.clickRate)} />
        </TableDataRow>
      ))
    )}
  </Table>
);
